(ns minorodata.components.transformations.sql-generator.view
  "Pure view layer for the SQL generator (a.k.a. JSON-flattening) modal.
   Subscribes to the `:json-flattening/...` singleton and shared platform
   subs internally. Containers wrap this view in `ui.dialog/dialog`, pass
   `:on-close`, optionally set `:lock-table?` to make the input-table
   picker static, and inject right-side header buttons via
   `:header-actions`."
  (:require
   ["@dnd-kit/core" :as dnd-kit.core]
   ["@dnd-kit/sortable" :as dnd-kit.sortable]
   ["@dnd-kit/utilities" :as dnd-kit.utilities]
   ["@radix-ui/react-portal" :as RadixPortal]
   ["allotment" :refer [Allotment]]
   ["copy-to-clipboard" :as copy]
   ["lucide-react" :refer [ChevronDown CircleOff Code CopyX GripVertical
                           ListRestart Table X]]
   ["react" :as react]
   ["react-resize-detector" :refer [useResizeDetector]]
   [applied-science.js-interop :as js-interop]
   [clojure.set]
   [clojure.string :as c.string]
   [minorodata.app.handlers :refer [dispatch dispatch-sync]]
   [ui.data-grid.preview :as data-grid-preview]
   [minorodata.components.monaco.editor :as monaco.editor]
   [minorodata.components.spinner.core :as c.spinner]
   [minorodata.components.transformations.schema-tree :as c.schema-tree]
   [minorodata.components.transformations.sql-generator.sidebar :refer [json-flattening-sidebar]]
   [minorodata.json-flattening.model :refer [->json-flattening]]
   [minorodata.utils :as utils]
   [re-posh.core :as re-posh]
   [reagent.core :as reagent]
   [ui.button]
   [ui.chip]
   [ui.dialog]
   [ui.dropdown-menu :refer [dropdown-menu dropdown-menu-item]]
   [ui.fields :as fields :refer [switch]]
   [ui.popover]
   [ui.tabs :as tabs]
   [ui.typography :as typography]))


(defn index-of [s v]
  (loop [idx 0 items s]
    (cond
      (empty? items) nil
      (= v (first items)) idx
      :else (recur (inc idx) (rest items)))))


(defn loading-table []
  [:div {:style {:display "flex" :flex-direction "row" :width "100%" :padding "20px"} :class "animate-kleene-pulse "}

   [:div {:class "p-2 flex flex-col gap-3 *:bg-kleene-neutral-04 dark:*:bg-kleene-dark-04 *:rounded-xs *:h-4 w-[10%]"}
    [:div]
    [:div]
    [:div]
    [:div]
    [:div]]

   [:div {:class "p-2 flex flex-col gap-3 *:bg-kleene-neutral-04 dark:*:bg-kleene-dark-04 *:rounded-xs *:h-4 w-[5%]"}
    [:div]
    [:div]
    [:div]
    [:div]]

   [:div {:class "p-2 flex flex-col gap-3 *:bg-kleene-neutral-04 dark:*:bg-kleene-dark-04 *:rounded-xs *:h-4 grow"}
    [:div]
    [:div]
    [:div]
    [:div]
    [:div]]

   [:div {:class "p-2 flex flex-col gap-3 *:bg-kleene-neutral-04 dark:*:bg-kleene-dark-04 *:rounded-xs *:h-4 grow"}
    [:div]
    [:div]
    [:div]]

   [:div {:class "p-2 flex flex-col gap-3 *:bg-kleene-neutral-04 dark:*:bg-kleene-dark-04 *:rounded-xs *:h-4 grow"}
    [:div]
    [:div]
    [:div]
    [:div]
    [:div]
    [:div]
    [:div]]])




(defonce tooltip-open? (reagent/atom false))

(defn copy-button [{:keys [text value disabled]}]
  [ui.popover/popover {:content [:span "Copied!"]
                       :placement :bottom
                       :open @tooltip-open?}
   [:span
    [ui.button/button
     {:variant  :primary-border
      :on-click (fn []
                  (copy value
                        #js {:format "text/plain"})
                  (reset! tooltip-open? true)

                  (js/setTimeout (fn []
                                   (reset! tooltip-open? false))
                                 1000))
      :disabled disabled
      :data-cy  "json-flattening-copy-btn"}
     text]]])

(defonce tab (reagent/atom :table))

(defn close-button [{:keys [changed? on-close]}]
  (let [[open? set-open] (react/useState false)
        close-dialog #(set-open false)
        open-dialog  #(set-open true)]
    [:<>
     [ui.button/button
      {:icon     X
       :variant  :empty
       :size     :icon-lg
       :on-click (if changed? open-dialog on-close)

       :data-cy "json-flattening-header-close-btn"}]
     [ui.dialog/dialog {:open           open?
                        :on-open-change set-open
                        :content-props  {:size :small}
                        :data-cy        "transform-json-flattening-close-dialog"
                        :title "Are you sure you want to close this window?"
                        :content [:div
                                  [:div {:class "py-2"}
                                   [:p "You'll lose your progress and would have to start over again"]]

                                  [ui.dialog/dialog-actions
                                   [ui.button/button {:auto-focus true
                                                      :variant :empty
                                                      :on-click   close-dialog
                                                      :data-cy    "transform-rollback-cancel-btn"}
                                    "Cancel"]
                                   [ui.dialog/dialog-close {:as-child true
                                                            :data-cy  "transform-do-rollback-btn"}
                                    [ui.button/button {:on-click on-close}
                                     "Close and lose progress"]]]]}]]))

(defn arrow-icon [text]
  (let [dark-mode? @(re-posh/subscribe [:app/prop :app/dark-mode?])]
    [:div {:class  "h-full w-full flex items-center justify-center"}
     [:img {:src (if dark-mode?
                   "/img/ui/arrow-left-bright.svg"
                   "/img/ui/arrow-left.svg")
            :class "mr-2 -mt-1"}]
     text]))

(defn resize-modal-handler [props]
  (let [{:keys [height]} (js->clj props :keywordize-keys true)]
    (dispatch [:json-flattening/update (->json-flattening {:editor-height height})])))

(defn trim-db-schema-table [column]
  (->> (clojure.string/split column #"\.")
       (drop 3)
       (clojure.string/join ".")))

(defn sortable-row [{column :id draggable :draggable values :values
                     updater-fn :updater-fn over? :over?
                     multiple-selection :multiple-selection
                     toggle-selected :multiple-select-fn
                     dnd :dnd}]
  (let [{:keys [attributes listeners set-node-ref set-activator-node-ref style]} dnd
        data-type-value (get-in values [column :data-type])
        name-value (get-in values [column :name])

        selected? (contains? multiple-selection column)]
    [:div
     (merge attributes
            {:ref set-node-ref
             :style style
             :key column
             :id  column
             :on-click (fn click-handler [e]
                         (let [ctrl? (js-interop/get e :ctrlKey)
                               meta? (js-interop/get e :metaKey)]
                           (when (or ctrl? meta?)
                             (toggle-selected column))))
             :class [(when draggable "cursor-grabbing max-w-125")
                     "relative flex flex-row w-full gap-2 select-none items-center py-1 pr-1"
                     (if selected?
                       "bg-kleene-hover-background"
                       "bg-kleene-background")
                     (when over? "border-t border-t-kleene-secondary-03")]})

     (when (and draggable selected?)
       [:div {:class "absolute -top-4 left-4"}
        [ui.chip/chip {:variant :secondary}
         (str (count multiple-selection) " columns")]])

     [:> GripVertical (merge
                       listeners
                       {:ref set-activator-node-ref
                        :size 20
                        :class [(when draggable "")
                                (when-not draggable "cursor-grab")
                                "shrink-0 grow-0 text-kleene-icon opacity-90"]})]

     ;; Align left, but trim with ellipsis on the left, since we mostly care about the
     ;; end of the column name
     [:div {:class [(when-not draggable "grow shrink")
                    (when draggable "w-full")
                    "overflow-hidden text-ellipsis rtl text-left"]
            :dir   "rtl"}
      (trim-db-schema-table column)]

     ;; hiding inputs for the drag placeholder
     (when-not draggable
       [:div {:class ["grow-0 shrink-0 w-50 flex flex-row items-stretch"]}

        (let [update-data-type (fn [value]
                                 (let [cache' (updater-fn column :data-type value)]
                                   (js/setTimeout
                                    #(c.schema-tree/request-new-preview cache')
                                    500)))]
          [dropdown-menu {:props {:align "start"}
                          :content [:div
                                    [dropdown-menu-item {:on-click (fn [] (update-data-type "text"))}
                                     "text"]
                                    [dropdown-menu-item {:on-click (fn [] (update-data-type "integer"))}
                                     "integer"]
                                    [dropdown-menu-item {:on-click (fn [] (update-data-type "float"))}
                                     "float"]
                                    [dropdown-menu-item {:on-click (fn [] (update-data-type "boolean"))}
                                     "boolean"]
                                    [dropdown-menu-item {:on-click (fn [] (update-data-type "datetime"))}
                                     "datetime"]]}
           [:div
            [ui.button/button {:size       :sm
                               :variant    :secondary
                               :data-index 0
                               :title      "Select preset type"
                               :class      "rounded-r-none border-r-0 h-full w-3"
                               :icon       ChevronDown}]]])

        [fields/input
         {:field        {:value  data-type-value
                         :action (fn [e]
                                   (updater-fn column :data-type (js-interop/get-in e [:target :value])))}

          :placeholder  "Data type"
          :input-props {:on-blur (fn []
                                   (js/setTimeout
                                    #(c.schema-tree/request-new-preview values)
                                    500))}
          :id "column-type"
          :errors      {"column-type" (when (or (not data-type-value)
                                                (clojure.string/blank? data-type-value))
                                        "can not be empty")}
          :required true
          :class      "min-w-40 rounded-l-none"
          :full-width false
          :native     false}]])


     (when-not draggable
       [:div {:class ["grow-0 shrink-0 w-75"]}
        [fields/input
         {:field       {:value  name-value
                        :action (fn [e]
                                  (updater-fn column :name (js-interop/get-in e [:target :value])))}
          :id "column-name"
          :errors      {"column-name" (when (or (not name-value)
                                                (clojure.string/blank? name-value))
                                        "can not be empty")}
          :input-props {:on-blur (fn []
                                   (js/setTimeout
                                    #(c.schema-tree/request-new-preview values)
                                    500))}
          :required true
          :full-width true
          :placeholder "Column name"
          :variant     "standard"}]])]))

(defn sortable-node
  "Live sortable row: registers itself with dnd-kit via `useSortable` and
  forwards the resolved drag bindings into `sortable-row`. Use `sortable-row`
  directly for the DragOverlay clone so we don't register two sortables under
  the same id."
  [{column :id :as props}]
  (let [params (dnd-kit.sortable/useSortable
                #js {:id column
                     :animateLayoutChanges dnd-kit.sortable/AnimateLayoutChanges})
        {:keys [attributes listeners setNodeRef transform transition setActivatorNodeRef]}
        (js->clj params :keywordize-keys true)
        style {:transform (js-interop/call-in dnd-kit.utilities [:CSS :Transform :toString]
                                              ;; Lock horizontal movement
                                              (js-interop/assoc! transform :x 0))
               :transition transition}]
    [sortable-row
     (assoc props :dnd {:attributes attributes
                        :listeners listeners
                        :set-node-ref setNodeRef
                        :set-activator-node-ref setActivatorNodeRef
                        :style style})]))

(defn vec-remove
  "Remove elem in coll by index."
  [coll pos]
  (vec (concat (subvec coll 0 pos) (subvec coll (inc pos)))))

(defn vec-add
  "Add elem in coll by index."
  [coll pos el]
  (vec (concat (subvec coll 0 pos) [el] (subvec coll pos))))

(defn vec-move
  "Move elem in coll by index"
  [coll pos1 pos2]
  (let [el (nth coll pos1)]
    (if (= pos1 pos2)
      coll
      (into [] (vec-add (vec-remove coll pos1) pos2 el)))))

(defn move-ordered-columns [{:keys [ordered-columns id place]}]
  (let [id-position (index-of ordered-columns id)
        move-to (index-of ordered-columns place)]
    (vec-move ordered-columns id-position move-to)))

(defn save-ordered-columns [ordered-columns]
  (dispatch [:json-flattening/update (->json-flattening {:ordered-columns ordered-columns})]))

(defn strip-table-prefix
  "Strip a literal `cat.schem.tbl.` prefix from `key-id`. Avoids regex so that
  identifiers containing regex metacharacters are handled correctly."
  [key-id {:keys [table-cat table-schem table-name]}]
  (let [prefix (str table-cat "." table-schem "." table-name ".")]
    (if (c.string/starts-with? key-id prefix)
      (subs key-id (count prefix))
      key-id)))

(defn view
  "Body of the SQL-generator dialog. Containers wrap this in
  `ui.dialog/dialog` and supply:

  - `:on-close` — called when the user closes the modal (via the X button
    or the unsaved-changes confirmation).
  - `:lock-table?` — when truthy, render the input-table picker as static
    text (no dropdown chevron, no metadata-tree picker). Used when the
    table is fixed by the entry point (e.g. PE \"+\" on a table node).
  - `:header-actions` — hiccup rendered between the title and the close
    button. Containers compose the right-side action buttons (group-select,
    copy, insert) here."
  [{:keys [lock-table? on-close header-actions]}]
  (let [width            @(re-posh/subscribe [:json-flattening/prop :json-flattening/sidebar-width])
        height           @(re-posh/subscribe [:json-flattening/prop :json-flattening/editor-height])
        code             @(re-posh/subscribe [:json-flattening/prop :json-flattening/code])
        selected-table   @(re-posh/subscribe [:json-flattening/prop :json-flattening/selected-table])
        server-data      @(re-posh/subscribe [:json-flattening/prop :json-flattening/data])
        preview-loading? @(re-posh/subscribe [:mold-http/loading? :json-flattening.api/fetch-sql])
        structure        @(re-posh/subscribe [:json-flattening/prop :json-flattening/structure])
        error            @(re-posh/subscribe [:json-flattening/prop :json-flattening/error])

        ordered-columns-db  @(re-posh/subscribe [:json-flattening/prop :json-flattening/ordered-columns])

        [ordered-columns set-ordered-columns] (react/useState ordered-columns-db)

        sensors (dnd-kit.core/useSensors (dnd-kit.core/useSensor dnd-kit.core/PointerSensor))

        [active-column-id set-active-column-id] (react/useState nil)
        [over-column-id set-over-column-id] (react/useState nil)

        [multiple-selection set-multiple-selection] (react/useState #{})

        mark-selected (fn mark-selected [column-id]
                        (set-multiple-selection
                         (fn [s]
                           (if (contains? s column-id)
                             (disj s column-id)
                             (conj s column-id)))))

        [configuration-cache update-configuration-cache] (react/useState {})

        [use-long-names set-use-long-names] (react/useState false)

        ;; Necessary to overcome the stale state issue in the
        ;; `fetch-new-preview` callback
        cache-ref (react/useRef configuration-cache)

        update-field-value (fn [id field value]
                             (let [cache' (assoc-in configuration-cache [id field] value)]
                               (update-configuration-cache cache')
                               (js-interop/assoc! cache-ref :current cache')
                               cache'))

        fetch-new-preview (fn []
                            (c.schema-tree/request-new-preview (js-interop/get cache-ref :current)))

        long-names-toggle (fn []
                            (let [use-long-names' (not use-long-names)
                                  columns (:json-structure (c.schema-tree/replace-default-node structure))

                                  configuration-cache'
                                  (reduce (fn [new-cache [k v]]
                                            (let [node (utils/find-by-field columns :key-id k)
                                                  short-name (:key-name node)
                                                  long-name  (-> (strip-table-prefix (:key-id node) selected-table)
                                                                 (c.string/replace "." "_"))

                                                  current-name (:name v)
                                                  generated-name? (or (= current-name short-name)
                                                                      (= current-name long-name))]
                                              (if generated-name?
                                                (if use-long-names'
                                                  (assoc new-cache k (assoc v :name long-name))
                                                  (assoc new-cache k (assoc v :name short-name)))
                                                (assoc new-cache k v))))
                                          {}
                                          configuration-cache)]

                              (update-configuration-cache configuration-cache')
                              (js-interop/assoc! cache-ref :current configuration-cache')
                              (set-use-long-names use-long-names')
                              (c.schema-tree/request-new-preview configuration-cache')))

        dark-mode? @(re-posh/subscribe [:app/prop :app/dark-mode?])

        [columns sorted-data]
        (react/useMemo
         (fn []
           (let [columns       (or (:columns server-data) [])
                 rows          (mapv (fn [cols] (apply assoc {} (interleave columns cols)))
                                     (or (:preview server-data) []))
                 fields-getter (mapv (fn [field] #(get % field)) columns)]
             [columns (mapv (apply juxt fields-getter) rows)]))
         #js [server-data])

        {:keys [ref]} (js->clj (useResizeDetector #js {:handleWidth  true
                                                       :handleHeight true
                                                       :refreshMode  "throttle"
                                                       :refreshRate  500
                                                       :onResize     resize-modal-handler})
                               :keywordize-keys true)]

    (react/useEffect
     (fn []
       (dispatch [:sql-console.api/fetch-metadata {:object "catalogs"}])

       ;; `width` doubles as the "record exists" check — the seed below sets
       ;; it, and `:json-flattening/delete` runs on unmount, so a nil width at
       ;; mount means there's no singleton yet.
       (when-not width
         (dispatch-sync [:json-flattening/add (->json-flattening {:sidebar-width 400 :editor-height 300})]))

       (fn []
         (reset! tooltip-open? false)
         (dispatch [:json-flattening/delete])))
     #js [])

    (react/useEffect
     (fn []
       (set-ordered-columns (or ordered-columns-db []))
       (fn []))
     #js [ordered-columns-db])


    ;; Prefill empty configuration cache values when necessary
    (react/useEffect
     (fn []
       (let [columns (:json-structure structure)
             cache' (reduce (fn [acc k]
                              (if (get acc k)
                                acc
                                (let [node (utils/find-by-field columns :key-id k)

                                      short-name (:key-name node)
                                      long-name  (-> (strip-table-prefix (:key-id node) selected-table)
                                                     (c.string/replace "." "_"))

                                      name (if use-long-names
                                             long-name
                                             short-name)
                                      data-type (:data-type node)]
                                  (assoc acc k {:name name
                                                :data-type (cond
                                                             ;; Keep allowed types
                                                             (some #{data-type} ["text" "integer" "float" "boolean" "datetime"]) data-type
                                                             ;; default to text
                                                             :else "text")}))))
                            configuration-cache
                            ordered-columns-db)]
         (update-configuration-cache cache')
         (js-interop/assoc! cache-ref :current cache'))

       (fn []))
     #js [ordered-columns-db])


    [:div {:class "h-full w-full flex flex-col"}
     [:header {:data-cy "json-flattening-header"
               :class   "flex justify-between items-center px-4 py-3 border-b"}
      [ui.dialog/dialog-title "SQL generator"]

      [:div {:class "flex gap-2 flex-1 justify-end"}
       header-actions

       [:div {:class "w-px self-stretch bg-kleene-layout-border"}]

       [:f> close-button {:changed? (not-empty ordered-columns)
                          :on-close on-close}]]]

     [:div {:class "h-full w-full overflow-hidden"
            :data-cy "versions-list-body"}
      [:> Allotment {:class "w-full"
                     :default-sizes [20 80]}
       [:> Allotment.Pane {:min-size 350
                           :max-size 600
                           :preferredSize width}
        [json-flattening-sidebar {:fetch-new-preview fetch-new-preview
                                  :lock-table?       lock-table?}]]

       (cond
         (empty? selected-table)
         [arrow-icon "start with selecting an input table"]

         (empty? ordered-columns)
         [arrow-icon "select table columns to preview the SQL"]

         :else
         [:> Allotment {:vertical true
                        :min-size 200}

          [:div {:class "h-full w-full grow overflow-y-scroll overflow-x-hidden"}

           [:div {:class "flex  pt-4 px-4 mb-6"}
            [typography/h3 {:class "grow"}  "Selected columns"]

            [ui.button/button {:size       :small
                               :variant    :empty
                               :title      "Reset order"
                               :icon       CircleOff
                               :on-click   (fn []
                                             (let [columns (:json-structure (c.schema-tree/replace-default-node structure))
                                                   nodes (mapv #(utils/find-by-field columns :key-id %) ordered-columns)
                                                   sorted-nodes (c.schema-tree/sort-by-read-order nodes)
                                                   ordered-columns' (mapv :key-id sorted-nodes)]
                                               (save-ordered-columns ordered-columns')
                                               (js/setTimeout
                                                #(c.schema-tree/request-new-preview configuration-cache)
                                                500)))}
             "Reset column order"]]

           (when-not (empty? ordered-columns)
             [:div {:class "px-4 gap-2 w-full"}

              [:div {:class "border-b border-b-slate-200 pb-1"}
               [:div {:class "flex flex-row w-full gap-2"}
                [:> GripVertical {:class "shrink-0 mt-1.5 invisible"}]

                [:div {:class "grow shrink flex flex-row gap-4 items-center"}
                 [:div "Field"]
                 (when-not (empty? multiple-selection)

                   [ui.button/button {:size       :icon-xs
                                      :variant    :empty
                                      :title      "Clear selection"
                                      :on-click   #(set-multiple-selection #{})
                                      :icon       CopyX
                                      :component  :div
                                      :class "mr-5 cursor-pointer"}
                    [:span {:class "text-xs"}
                     (str "Clear multiple selection (" (count multiple-selection) ")")]])]

                [:div {:class "grow-0 w-50 shrink-0"} "Data type"]
                [:div {:class "grow-0 w-75 shrink-0"}
                 [:div {:class "flex flex-row gap-2 justify-between"}
                  [:div "Column name"]
                  [:div {:class "flex flex-row gap-2"}
                   [:div {:class "text-sm font-medium"
                          :on-click long-names-toggle}
                    "Short"]
                   [switch {:id       :switch
                            :label    "Full"
                            :value    use-long-names
                            :on-click long-names-toggle}]]]]]]


              [:> dnd-kit.core/DndContext
               {:sensors sensors
                :onDragOver (fn [e]
                              (let [{:keys [over]} (js->clj e :keywordize-keys true)
                                    target-id (:id over)]

                                ;; Dragging over this item
                                (set-over-column-id target-id)))
                :onDragStart (fn [e]
                               (let [{:keys [active]} (js->clj e :keywordize-keys true)
                                     origin-id (:id active)]
                                 ;; Clear selection if dragging something that is not selected
                                 (when-not (contains? multiple-selection origin-id)
                                   (set-multiple-selection #{})))

                               (set-active-column-id (js-interop/get-in e [:active :id])))
                :onDragEnd (fn [e]
                             (set-active-column-id nil)
                             (set-over-column-id nil)

                             ;; By this time `multiple-selection` should be empty if the user started
                             ;; dragging unselected column
                             (if (empty? multiple-selection)

                               ;; Reorder just one item that was being dragged
                               (let [{:keys [over active]} (js->clj e :keywordize-keys true)
                                     target-id (:id over)
                                     origin-id (:id active)]
                                 ;; when not dragging and dropping over itself
                                 (when (not= target-id origin-id)
                                   (let [ordered-columns' (move-ordered-columns {:ordered-columns ordered-columns
                                                                                 :id origin-id
                                                                                 :place target-id})]
                                     ;; Update local state first to prevent scroll position being lost
                                     (set-ordered-columns ordered-columns')
                                     (save-ordered-columns ordered-columns'))))

                               ;; Reorder the selected group
                               (let [{:keys [over]} (js->clj e :keywordize-keys true)
                                     target-id (:id over)
                                     ordered-group-being-moved (vec (filter #(contains? multiple-selection %) ordered-columns))

                                     splice-position (index-of ordered-columns target-id)
                                     parts (split-at splice-position ordered-columns)

                                     ;; two parts without the columns being moved
                                     [beginning end] (->> parts
                                                          (map (partial remove multiple-selection))
                                                          (map vec))

                                     ordered-columns' (vec (concat beginning ordered-group-being-moved end))]
                                 ;; Update local state first to prevent scroll position being lost
                                 (set-ordered-columns ordered-columns')
                                 (save-ordered-columns ordered-columns')))

                             ;; Clear selection when not dragging anymore
                             (set-multiple-selection #{}))}

               [:div {:class "flex flex-col divide-y divide-solid"}
                (into
                 [:> dnd-kit.sortable/SortableContext
                  {:items ordered-columns
                   :strategy dnd-kit.sortable/verticalListSortingStrategy
                   :class ""}]

                 (map
                  (fn [column]
                    ^{:key column}
                    [:f> sortable-node {:id column
                                        :over? (= column over-column-id)
                                        :values configuration-cache
                                        :multiple-selection multiple-selection
                                        :multiple-select-fn mark-selected
                                        :updater-fn update-field-value}])
                  ordered-columns))]

               [:> RadixPortal/Root {:as-child true}
                [:> dnd-kit.core/DragOverlay {:zIndex 2200}
                 (when active-column-id
                   [sortable-row {:id active-column-id
                                  :draggable true
                                  :multiple-selection multiple-selection}])]]]])]

          [:div {:class "h-full"}
           [:div {:class "h-full p-0"}
            [:header {:class "flex gap-2 justify-between items-center"}
             [tabs/tabs-root {:value @tab :on-value-change #(reset! tab (keyword %)) :variant :tab :class "flex-1"}
              [tabs/tabs-list {:variant :tab :class "px-4"}
               [tabs/tabs-trigger {:value :code :variant :tab}
                [:> Code {:size 16 :class "mr-2"}] "Code"]
               [tabs/tabs-trigger {:value :table :variant :tab}
                [:> Table {:size 16  :class "mr-2"}] "Table"]

               [:div {:class "flex-1 flex justify-end"}
                [ui.button/button {:variant    :empty-transparent
                                   :size       :small
                                   :on-click   #(c.schema-tree/request-new-preview configuration-cache)
                                   :icon       ListRestart}
                 "Reload"]]]]]

            (if (or preview-loading?
                    (c.string/blank? code)
                    (empty? ordered-columns)
                    (empty? selected-table))
              (if preview-loading?

                [:div {:class "h-full w-full flex items-center justify-center"}
                 [c.spinner/inline-configurable {:color :primary
                                                 :size 100}]]

                [:div {:class "h-full w-full"}
                 [loading-table]
                 [:div {:class "absolute bottom-5 left-0 right-9 text-center"}
                  [:p {:class "text-kleene-text/50"}
                   "Table with 1000 random rows of data will be shown here"]]])

              (if (not (clojure.string/blank? error))
                [:div {:class "p-4"}
                 [:div {:class ""}
                  [typography/h3 {:class "pb-4"}
                   "Failed to generate SQL:"]]

                 [:div {:class ""}
                  [:pre {:class "text-kleene-error-01 border rounded-xs border-kleene-error-01 p-2 text-wrap"}
                   (str error)]]]

                [:div {:class "h-full w-full grow overflow-hidden"
                       :ref ref}
                 (if (= :code @tab)

                   ^{:key code}
                   [:f> monaco.editor/editor
                    {:language            "snowflake"
                     :theme               (if dark-mode? "vs-dark" "vs")
                     :data-cy             "sql-console-editor"
                     :options             {:fontSize        14
                                           :automaticLayout true
                                           :readOnly        true
                                           :contextmenu     true}
                     :value               (or code "")}]

                   (let [preview-height    (when height (js/Math.ceil height))
                         height-expression (str "calc(" preview-height "px - 40px)")]

                     [:f> data-grid-preview/table
                      {:headers                   columns
                       :rows                      sorted-data
                       :dark-mode?                dark-mode?
                       :height                    height-expression
                       :column-width              150
                       :sortable?                 true
                       :filterable?               true
                       :resizable?                true
                       :row-numbers?              true
                       :suppress-no-rows-overlay? true}]))]))]]])]]]))
